import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const OxygenTankDurationCalculator = () => {
  const [oxygenData, setOxygenData] = useState({
    tankCapacity: '',
    flowRate: ''
  });

  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setOxygenData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const calculateDuration = () => {
    const { tankCapacity, flowRate } = oxygenData;

    const tankCapacityNum = parseFloat(tankCapacity) || 0;
    const flowRateNum = parseFloat(flowRate) || 0;

    if (tankCapacityNum === 0 || flowRateNum === 0) {
      setError('Please enter valid tank capacity and flow rate');
      return;
    }

    const durationHours = tankCapacityNum / flowRateNum;

    const newResults = {
      durationHours
    };

    setResults(newResults);

    setChartData({
      labels: ['Tank Capacity', 'Flow Rate'],
      datasets: [
        {
          label: 'Oxygen Usage',
          data: [tankCapacityNum, flowRateNum],
          backgroundColor: ['rgba(75, 192, 192, 0.5)', 'rgba(255, 99, 132, 0.5)'],
          borderColor: ['rgba(75, 192, 192, 1)', 'rgba(255, 99, 132, 1)'],
          borderWidth: 1
        }
      ]
    });
  };

  const exampleData = {
    tankCapacity: 60, // in liters
    flowRate: 2, // in liters per minute
    durationHours: 30 // calculated duration
  };

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <Helmet>
        <title>Oxygen Tank Duration Calculator | Calculate Oxygen Tank Duration</title>
        <meta name="description" content="Calculate how long your oxygen tank will last with our Oxygen Tank Duration Calculator. Enter tank capacity and flow rate to get the duration." />
        <meta name="keywords" content="Oxygen tank duration calculator, oxygen tank, flow rate, tank capacity, medical calculator" />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <div className="flex items-center mb-8">
            <div className="text-4xl text-blue-600 mr-4">oxy</div>
            <h1 className="text-3xl font-bold text-gray-800">Oxygen Tank Duration Calculator</h1>
          </div>

          <div className="text-gray-600 mb-8">
            <p>The Oxygen Tank Duration Calculator helps you determine how long your oxygen tank will last based on its capacity and your flow rate.</p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-white rounded-lg shadow-lg p-6"
          >
            <div className="grid grid-cols-1 gap-6 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tank Capacity (in liters):</label>
                <input
                  type="number"
                  id="tankCapacity"
                  name="tankCapacity"
                  value={oxygenData.tankCapacity}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter tank capacity in liters"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Flow Rate (in liters per minute):</label>
                <input
                  type="number"
                  id="flowRate"
                  name="flowRate"
                  value={oxygenData.flowRate}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter flow rate in liters per minute"
                />
              </div>
            </div>

            <button
              onClick={calculateDuration}
              className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors duration-200"
            >
              Calculate Duration
            </button>

            {error && (
              <div className="mt-4 p-4 text-red-700 bg-red-100 rounded-lg">
                {error}
              </div>
            )}

            {results && (
              <div className="mt-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-6">Oxygen Tank Duration Analysis</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">Tank Capacity</h3>
                    <p className="text-gray-600">{results.tankCapacity} liters</p>
                  </div>

                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">Flow Rate</h3>
                    <p className="text-gray-600">{results.flowRate} liters per minute</p>
                  </div>
                </div>

                {chartData && (
                  <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                    <Bar
                      data={chartData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                          y: {
                            beginAtZero: true,
                            title: {
                              display: true,
                              text: 'Liters'
                            }
                          },
                          x: {
                            title: {
                              display: true,
                              text: 'Categories'
                            }
                          }
                        },
                        plugins: {
                          title: {
                            display: true,
                            text: 'Oxygen Usage Breakdown',
                            font: {
                              size: 16
                            }
                          }
                        }
                      }}
                    />
                  </div>
                )}

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Duration</h3>
                  <p className="text-gray-600">Your oxygen tank will last approximately {results.durationHours.toFixed(2)} hours.</p>
                </div>
              </div>
            )}
          </motion.div>

          <div className="mt-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Example Calculation <span className="text-blue-600">📝</span></h2>
            <div className="bg-white p-8 rounded-lg shadow-md">
              <p className="text-gray-700 mb-6">
                Here's an example calculation using sample data:
              </p>
              
              <div className="bg-gray-50 p-6 rounded-lg">
                <h4 className="font-semibold mb-4">Sample Oxygen Data:</h4>
                <ul className="list-disc pl-8 space-y-2">
                  <li>Tank Capacity: {exampleData.tankCapacity} liters</li>
                  <li>Flow Rate: {exampleData.flowRate} liters per minute</li>
                </ul>
                
                <h4 className="font-semibold mt-6 mb-4">Calculations:</h4>
                <ol className="list-decimal pl-8 space-y-4">
                  <li>
                    <p>Duration Calculation:</p>
                    <ul className="list-none pl-8 space-y-2">
                      <li>Duration (in hours) = Tank Capacity / Flow Rate = {exampleData.tankCapacity} / {exampleData.flowRate} = {exampleData.durationHours} hours</li>
                    </ul>
                  </li>
                </ol>
                
                <p className="mt-6 font-semibold">Interpretation:</p>
                <p>In this example, with a tank capacity of {exampleData.tankCapacity} liters and a flow rate of {exampleData.flowRate} liters per minute, the oxygen tank will last approximately {exampleData.durationHours} hours.</p>
              </div>
            </div>
          </div>

          <div className="mt-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Understanding Oxygen Tank Duration <span className="text-blue-600">📊</span></h2>
            <div className="text-gray-600 mb-8">
              <p>The Oxygen Tank Duration Calculator helps you determine how long your oxygen tank will last based on its capacity and your flow rate. This is useful for:</p>
              <ul className="list-disc pl-6 mt-2">
                <li>Medical planning and preparedness</li>
                <li>Ensuring you have enough oxygen for your needs</li>
                <li>Managing oxygen supply in medical facilities</li>
              </ul>
              <p className="italic mt-2">Note: Always consult a healthcare professional for medical advice.</p>
            </div>
          </div>

          <div className="mt-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">How to Use Oxygen Tank Duration Calculator <span className="text-blue-600">📋</span></h2>
            <div className="text-gray-600">
              <p>Follow these steps to use the Oxygen Tank Duration Calculator effectively:</p>
              
              <div className="mt-4">
                <h3 className="text-lg font-semibold text-blue-600 mb-2">Step 1: Enter Your Data</h3>
                <ul className="list-disc pl-6 mt-2">
                  <li>Enter the tank capacity in liters</li>
                  <li>Enter the flow rate in liters per minute</li>
                </ul>
              </div>

              <div className="mt-6">
                <h3 className="text-lg font-semibold text-blue-600 mb-2">Step 2: Calculate Duration</h3>
                <ul className="list-disc pl-6 mt-2">
                  <li>Click "Calculate Duration" to generate the results</li>
                  <li>View the duration in hours that your oxygen tank will last</li>
                </ul>
              </div>

              <div className="mt-6">
                <h3 className="text-lg font-semibold text-blue-600 mb-2">Step 3: Interpret Results</h3>
                <ul className="list-disc pl-6 mt-2">
                  <li>Analyze the duration to plan your oxygen usage</li>
                  <li>Use the chart to visualize the oxygen usage breakdown</li>
                </ul>
              </div>

              <div className="mt-6 bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-blue-600 mb-2">Important Considerations</h3>
                <ul className="list-disc pl-6 mt-2">
                  <li>Ensure accurate measurements for tank capacity and flow rate</li>
                  <li>Consult a healthcare professional for medical advice</li>
                  <li>Use this tool to plan and manage your oxygen supply effectively</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OxygenTankDurationCalculator;